/**
 * Editable form for reviewing extracted document data.
 *
 * Displays fields organized by category with provenance tooltips
 * that show the source text and page number for each field.
 */

import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  User,
  DollarSign,
  Calendar,
  Building,
  FileText,
  Info,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { FieldProvenance, ClassificationInfo, PortfolioInvestment } from "@/lib/extraction-api";
import { getDocumentPortfolio } from "@/lib/extraction-api";
import { EditablePortfolioTable } from "./EditablePortfolioTable";

interface ReviewFormProps {
  /** Extracted data fields */
  data: Record<string, unknown>;
  /** Provenance data mapping fields to source text/pages */
  provenance: Record<string, FieldProvenance | Record<string, FieldProvenance>> | null;
  /** Classification info for the document */
  classification: ClassificationInfo | null;
  /** Document type */
  documentType: string | null;
  /** Document ID (for fetching portfolio investments) */
  documentId?: string;
  /** Callback when a field is edited */
  onChange?: (field: string, value: unknown) => void;
  /** Callback when portfolio investments are edited */
  onPortfolioChange?: (investments: PortfolioInvestment[], hasChanges: boolean) => void;
  /** Callback when provenance button is clicked (to navigate PDF) */
  onProvenanceClick?: (page: number, text: string) => void;
  /** Whether the form is in read-only mode */
  readOnly?: boolean;
  /** Optional className */
  className?: string;
}

// Field categories for organized display
const fieldCategories: Record<string, { label: string; icon: React.ReactNode; fields: string[] }> = {
  identification: {
    label: "Identification",
    icon: <User className="h-4 w-4" />,
    fields: ["investor_entity", "gp_fund_manager", "fund_name", "currency"],
  },
  financial: {
    label: "Financial Details",
    icon: <DollarSign className="h-4 w-4" />,
    fields: [
      "capital_commitment",
      "call_amount",
      "call_percentage",
      "call_for_investments",
      "call_for_expenses",
      "remaining_commitment",
      "total_capital_called",
      "distribution_amount",
      "gross_distribution",
      "tax_withheld",
      "capital_balance",
      "total_contributions",
      "total_distributions",
      "total_recallable_distributions",
      "total_non_recallable_distributions",
      "total_partners_capital",
      "net_asset_value",
    ],
  },
  dates: {
    label: "Dates",
    icon: <Calendar className="h-4 w-4" />,
    fields: [
      "notice_date",
      "due_date",
      "distribution_date",
      "payment_date",
      "capital_balance_date",
      "statement_date",
      "period_start",
      "period_end",
    ],
  },
  wire: {
    label: "Wire Instructions",
    icon: <Building className="h-4 w-4" />,
    fields: [
      "wire_bank_name",
      "wire_bank_address",
      "wire_routing_number",
      "wire_swift_code",
      "wire_account_number",
      "wire_beneficiary",
      "wire_reference",
    ],
  },
};

// Document type labels
const documentTypeLabels: Record<string, string> = {
  capital_call: "Capital Call",
  distribution_notice: "Distribution Notice",
  quarterly_account_statement: "Quarterly Account Statement",
  quarterly_financial_statement: "Quarterly Financial Statement",
  portfolio_statement_custodian: "Portfolio Statement (Custodian)",
  portfolio_statement_consolidated: "Portfolio Statement (Consolidated)",
};

// Confidence badge variants
const confidenceBadges: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
  high: { variant: "default", label: "High Confidence" },
  medium: { variant: "secondary", label: "Medium Confidence" },
  low: { variant: "destructive", label: "Low Confidence" },
  unknown: { variant: "outline", label: "Unknown Confidence" },
};

// Format field names for display
function formatFieldName(field: string): string {
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Determine field type for input formatting
function getFieldType(field: string): "currency" | "percentage" | "date" | "text" {
  if (
    field.includes("amount") ||
    field.includes("commitment") ||
    field.includes("balance") ||
    field.includes("contribution") ||
    field.includes("distribution") ||
    field.includes("called") ||
    field.includes("expenses") ||
    field.includes("investments") ||
    field.includes("capital") ||
    field.includes("nav") ||
    field.includes("asset_value")
  ) {
    return "currency";
  }
  if (field.includes("percentage") || field.includes("percent")) {
    return "percentage";
  }
  if (field.includes("date")) {
    return "date";
  }
  return "text";
}

// Format value for display
function formatValue(field: string, value: unknown, forDateInput: boolean = false): string {
  if (value === null || value === undefined) return "";

  const fieldType = getFieldType(field);

  if (fieldType === "currency" && typeof value === "number") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  }

  if (fieldType === "percentage" && typeof value === "number") {
    return `${(value * 100).toFixed(2)}%`;
  }

  if (fieldType === "date" && typeof value === "string") {
    if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
      // For date inputs, keep ISO format (YYYY-MM-DD)
      if (forDateInput) {
        return value.substring(0, 10);
      }
      return new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }

  return String(value);
}

// Parse input value back to appropriate type
function parseValue(field: string, inputValue: string): unknown {
  if (inputValue === "") return null;

  const fieldType = getFieldType(field);

  if (fieldType === "currency") {
    const num = parseFloat(inputValue.replace(/[^0-9.-]/g, ""));
    return isNaN(num) ? null : num;
  }

  if (fieldType === "percentage") {
    const num = parseFloat(inputValue.replace(/[^0-9.-]/g, ""));
    return isNaN(num) ? null : num / 100;
  }

  return inputValue;
}

interface FieldInputProps {
  field: string;
  value: unknown;
  provenance: FieldProvenance | null;
  onChange?: (field: string, value: unknown) => void;
  onProvenanceClick?: (page: number, text: string) => void;
  readOnly?: boolean;
}

function FieldInput({
  field,
  value,
  provenance,
  onChange,
  onProvenanceClick,
  readOnly,
}: FieldInputProps) {
  const fieldType = getFieldType(field);
  const isDateInput = fieldType === "date";
  const [inputValue, setInputValue] = useState(formatValue(field, value, isDateInput));

  useEffect(() => {
    setInputValue(formatValue(field, value, isDateInput));
  }, [field, value, isDateInput]);

  const handleBlur = () => {
    const parsed = parseValue(field, inputValue);
    if (parsed !== value) {
      onChange?.(field, parsed);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Label htmlFor={field} className="text-xs text-muted-foreground">
          {formatFieldName(field)}
        </Label>
        {provenance && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-1.5 text-xs text-primary hover:bg-primary/10"
                onClick={() => onProvenanceClick?.(provenance.page, provenance.text)}
              >
                p.{provenance.page}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs font-medium">Source (Page {provenance.page}):</p>
              <p className="text-xs text-muted-foreground mt-1">"{provenance.text}"</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Input
        id={field}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleBlur}
        readOnly={readOnly}
        type={fieldType === "date" ? "date" : "text"}
        className="h-8 text-sm"
      />
    </div>
  );
}

interface PortfolioSectionProps {
  documentId: string;
  pdfStatedTotals?: { totalCost?: number; totalFairValue?: number };
  portfolioProvenance?: Record<string, FieldProvenance> | null;
  onPortfolioChange?: (investments: PortfolioInvestment[], hasChanges: boolean) => void;
  onProvenanceClick?: (page: number, text: string) => void;
  readOnly?: boolean;
}

function PortfolioSection({
  documentId,
  pdfStatedTotals,
  portfolioProvenance,
  onPortfolioChange,
  onProvenanceClick,
  readOnly,
}: PortfolioSectionProps) {
  const { data: portfolioData, isLoading } = useQuery({
    queryKey: ["document-portfolio", documentId],
    queryFn: () => getDocumentPortfolio(documentId),
    enabled: !!documentId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const investments = portfolioData?.investments || [];

  return (
    <EditablePortfolioTable
      investments={investments}
      pdfStatedTotals={pdfStatedTotals}
      portfolioProvenance={portfolioProvenance}
      onChange={onPortfolioChange || (() => {})}
      onProvenanceClick={onProvenanceClick}
      readOnly={readOnly}
    />
  );
}

export function ReviewForm({
  data,
  provenance,
  classification,
  documentType,
  documentId,
  onChange,
  onPortfolioChange,
  onProvenanceClick,
  readOnly = false,
  className,
}: ReviewFormProps) {
  // Extract PDF-stated totals from document data for portfolio comparison
  const pdfStatedTotals = useMemo(() => {
    if (!data) return undefined;
    const totalCost = data.investments_cost as number | undefined;
    const totalFairValue = data.investments_fair_value as number | undefined;
    if (totalCost == null && totalFairValue == null) return undefined;
    return { totalCost, totalFairValue };
  }, [data]);

  // Extract portfolio investment provenance from document metadata
  const portfolioProvenance = useMemo(() => {
    if (!provenance) return null;
    const portProv = provenance.portfolio_investments;
    if (!portProv || typeof portProv !== "object") return null;
    // portfolio_investments provenance is a Record<investmentName, {page, text}>
    // Filter out entries that aren't FieldProvenance (have page+text)
    const result: Record<string, FieldProvenance> = {};
    for (const [name, value] of Object.entries(portProv)) {
      if (value && typeof value === "object" && "page" in value && "text" in value) {
        result[name] = value as FieldProvenance;
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  }, [provenance]);

  // Map wire field names to labels used in the provenance text
  const wireFieldLabels: Record<string, string[]> = {
    wire_bank_name: ["Bank Name:", "Bank:"],
    wire_bank_address: ["City, State:", "Address:", "Bank Address:"],
    wire_routing_number: ["ABA Number:", "Routing Number:", "ABA:"],
    wire_swift_code: ["SWIFT Code:", "SWIFT:", "BIC:"],
    wire_account_number: ["Account Number:", "Account:", "Acct:"],
    wire_beneficiary: ["For Credit To:", "Beneficiary:", "Credit To:"],
    wire_reference: ["Reference:", "Ref:", "For Further Credit:"],
  };

  // Get provenance for a field (handles wire fields mapped to wire_instructions)
  const getFieldProvenance = (field: string, fieldValue: unknown): FieldProvenance | null => {
    if (!provenance) return null;

    // Direct provenance lookup
    const p = provenance[field];
    if (p && typeof p === "object" && "page" in p && "text" in p) {
      return p as FieldProvenance;
    }

    // For wire_* fields, try to extract from wire_instructions provenance
    if (field.startsWith("wire_")) {
      const wireProv = provenance.wire_instructions;
      if (wireProv && typeof wireProv === "object" && "page" in wireProv && "text" in wireProv) {
        const wireText = (wireProv as FieldProvenance).text;
        const page = (wireProv as FieldProvenance).page;

        // Try to find the specific line for this field
        const labels = wireFieldLabels[field] || [];
        const lines = wireText.split("\n");

        for (const label of labels) {
          const matchingLine = lines.find(line => line.includes(label));
          if (matchingLine) {
            return { page, text: matchingLine.trim() };
          }
        }

        // Fallback: try to find the value in the text
        if (fieldValue && typeof fieldValue === "string") {
          const matchingLine = lines.find(line => line.includes(fieldValue));
          if (matchingLine) {
            return { page, text: matchingLine.trim() };
          }
        }

        // Last resort: return the whole wire_instructions provenance
        return wireProv as FieldProvenance;
      }
    }

    return null;
  };

  // Get all data fields not in predefined categories
  const categorizedFields = new Set(
    Object.values(fieldCategories).flatMap((cat) => cat.fields)
  );
  const otherFields = Object.keys(data).filter(
    (key) =>
      !categorizedFields.has(key) &&
      key !== "wire_instructions" &&
      !key.includes("timestamp") &&
      !key.includes("source_file")
  );

  const confidenceInfo = classification?.confidence
    ? confidenceBadges[classification.confidence]
    : confidenceBadges.unknown;

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header with document type and classification */}
      <div className="p-4 border-b space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <span className="font-semibold">
            {documentType ? documentTypeLabels[documentType] || documentType : "Document"}
          </span>
          {classification && (
            <Badge variant={confidenceInfo.variant}>{confidenceInfo.label}</Badge>
          )}
        </div>
        {classification?.confidence === "low" && (
          <div className="p-2 bg-destructive/10 rounded-md text-sm text-destructive flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Low confidence classification. Please verify the document type and
              extracted data carefully.
            </span>
          </div>
        )}
        {classification?.reasoning && (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-xs text-muted-foreground cursor-help truncate">
                {classification.reasoning}
              </p>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-md">
              <p className="text-xs">{classification.reasoning}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Scrollable form content */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Categorized Fields */}
          {Object.entries(fieldCategories).map(([key, category]) => {
            if (key === "wire_instructions") return null;

            const categoryData = category.fields.filter((field) => field in data);
            if (categoryData.length === 0) return null;

            return (
              <div key={key}>
                <div className="flex items-center gap-2 mb-3">
                  {category.icon}
                  <h4 className="font-semibold text-sm">{category.label}</h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {categoryData.map((field) => (
                    <FieldInput
                      key={field}
                      field={field}
                      value={data[field]}
                      provenance={getFieldProvenance(field, data[field])}
                      onChange={onChange}
                      onProvenanceClick={onProvenanceClick}
                      readOnly={readOnly}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Other Fields */}
          {otherFields.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-3">Additional Information</h4>
              <div className="grid grid-cols-2 gap-4">
                {otherFields.map((field) => (
                  <FieldInput
                    key={field}
                    field={field}
                    value={data[field]}
                    provenance={getFieldProvenance(field, data[field])}
                    onChange={onChange}
                    onProvenanceClick={onProvenanceClick}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Schedule of Investments (quarterly financial statements only) */}
          {documentType === "quarterly_financial_statement" && documentId && (
            <PortfolioSection
              documentId={documentId}
              pdfStatedTotals={pdfStatedTotals}
              portfolioProvenance={portfolioProvenance}
              onPortfolioChange={onPortfolioChange}
              onProvenanceClick={onProvenanceClick}
              readOnly={readOnly}
            />
          )}

        </div>
      </ScrollArea>
    </div>
  );
}

export default ReviewForm;
